import { useState } from "react";
import { useRenameWorkspace } from "./workspace.js";

/**
 * A workspace's name, typed over (ADR 0006 amendment, 2026-09-20).
 *
 * One form for both the places the list of workspaces is drawn — the account menu and the Workspace panel — because a
 * rename that behaved differently in the two would be two features to keep true. `small` is the menu's size and
 * nothing else; everything the person can do is the same in both.
 *
 * It is the label on the folder. The business's own name is a section of the Business page, and changing *that* is
 * what starts a lookup, which is why this one is free and instant and can be typed back.
 */
export function RenameWorkspace({ id, name, onDone, small = false }: {
  id: string;
  name: string;
  onDone: () => void;
  small?: boolean;
}) {
  const rename = useRenameWorkspace();
  const [typed, setTyped] = useState(name);
  const ready = typed.trim().length > 0 && !rename.isPending;
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (ready) rename.mutate({ id, name: typed.trim() }, { onSuccess: onDone }); }}
      className={`flex flex-col gap-1 ${small ? "px-3.5 py-1.5" : "px-3.5 py-2.5"}`}
    >
      <div className="flex items-center gap-2">
        <input
          autoFocus value={typed} onChange={(e) => setTyped(e.target.value)} maxLength={80}
          aria-label={`A new name for ${name}`}
          className={`flex-1 min-w-0 px-2.5 py-1.5 rounded-lg border border-line-2 bg-page ${small ? "text-[12.5px]" : "text-[13px]"}`}
        />
        <button type="submit" disabled={!ready} className="shrink-0 px-3 py-1.5 rounded-lg bg-ink text-white text-xs font-medium disabled:opacity-60">Save</button>
        <button type="button" onClick={() => { rename.reset(); onDone(); }} className="shrink-0 px-2 py-1.5 text-xs text-muted hover:text-ink">Cancel</button>
      </div>
      {rename.error ? <p role="alert" className="text-[12px] text-bad">{String(rename.error)}</p> : null}
    </form>
  );
}
